import {
  angleOf,
  dist,
  measurements,
  normalizeDegrees,
  segment,
  sub,
  type Anchor,
  type Measurement,
  type Shape,
  type Vec2,
} from '../../geom';
import type { ToolContext, ToolEvent } from '../types';
import { BaseTool } from './base';

const MAX_POINTS = 3;

/** The angle at `b` between the legs to `a` and to `c`, from 0° to 180°. */
function between(a: Vec2, b: Vec2, c: Vec2): number {
  const turn = normalizeDegrees(angleOf(sub(c, b)) - angleOf(sub(a, b)));
  return turn > 180 ? 360 - turn : turn;
}

/**
 * Measures between picked points, each snapping like any placed point. The first leg is
 * measured exactly as a selected line is, so its rows match; a third point adds the second
 * leg and the angle at the vertex. Rows follow the pointer until the points are placed, the
 * legs preview on the canvas, and nothing is added to the drawing. A click after the third
 * starts over.
 */
export class MeasureTool extends BaseTool {
  private points: Vec2[] = [];
  private cursor: Vec2 | null = null;

  status(): string {
    switch (this.points.length) {
      case 0:
        return 'Measure: click the first point';
      case 1:
        return 'Measure: click the second point';
      case 2:
        return 'Measure: click a third point for an angle, or Escape';
      default:
        return 'Measure: click to start again, or Escape';
    }
  }

  inProgress(): boolean {
    return this.points.length > 0;
  }

  /** The last placed point, so ortho, perpendicular and tangent measure from it. */
  override reference(): Anchor | null {
    const last = this.points.at(-1);
    return last && this.points.length < MAX_POINTS ? { point: last, mode: 'line' } : null;
  }

  override previews(cursor: Vec2): Shape[] {
    const placed = this.points.slice(1).map((point, i) => segment(this.points[i]!, point));
    const last = this.points.at(-1);
    return last && this.points.length < MAX_POINTS ? [...placed, segment(last, cursor)] : placed;
  }

  override measurements(): Measurement[] {
    // Points placed so far, then the pointer as the next one while more can be placed.
    const pending = this.points.length < MAX_POINTS && this.cursor ? [this.cursor] : [];
    const [a, b, c] = [...this.points, ...pending];
    if (!a || !b) return [];
    const rows = measurements(segment(a, b));
    if (!c) return rows;
    return [
      ...rows,
      { label: 'Second leg', kind: 'length', value: dist(b, c) },
      { label: 'Angle at vertex', kind: 'angle', value: between(a, b, c) },
    ];
  }

  override pointerDown(_ctx: ToolContext, event: ToolEvent): void {
    if (event.button !== 0) return;
    if (this.points.length === MAX_POINTS) this.points = [];
    this.points.push(event.point);
    // The pointer is on the point just placed; it becomes the next one only once it moves.
    this.cursor = null;
  }

  override pointerMove(_ctx: ToolContext, event: ToolEvent): void {
    this.cursor = event.point;
  }

  override cancel(): void {
    this.points = [];
    this.cursor = null;
  }
}
